import { Input } from '@builder.io/sdk';

const sectionFields = [
  {
    name: 'id',
    type: 'string',
    advanced: true,
    helperText: 'Unique ID for this section. Useful to create anchor links or for direct navigation.',
  },
  {
    name: 'paddingTop',
    type: 'string',
    defaultValue: 'lg',
    enum: ['none', 'xs', 'sm', 'md', 'lg', 'xl', '2xl'],
    advanced: true,
    helperText: 'Controls the top padding of the section. Adjusts vertical spacing above the content.',
  },
  {
    name: 'paddingBottom',
    type: 'string',
    defaultValue: 'lg',
    enum: ['responsive-none-mobile', 'none', 'xs', 'sm', 'md', 'lg', 'xl', '2xl'],
    advanced: true,
    helperText: 'Controls the bottom padding of the section. Adjusts vertical spacing below the content.',
  },
  {
    name: 'darkMode',
    type: 'boolean',
    defaultValue: true,
    advanced: true,
    helperText: 'Toggle dark mode for this section. Useful for consistent styling in dark-themed pages.',
  },
  {
    name: 'nextComponentIsDark',
    type: 'boolean',
    defaultValue: true,
    advanced: true,
    helperText: 'Indicates if the next section/component is in dark mode. Helps adjust transitions between sections.',
  },
  {
    name: 'hasGradientTransition',
    type: 'boolean',
    defaultValue: false,
    advanced: true,
    helperText: 'Enable gradient transition (Teal horizontal - Figma #1). Main gradient style for section separation.',
  },
  {
    name: 'hasLinearGradientTransition',
    type: 'boolean',
    defaultValue: false,
    advanced: true,
    showIf: "options.get('hasGradientTransition') === false",
    helperText:
      'Adds a linear gradient transition background (Blue soft gradient - Figma #2). Next component is dark has to be true.',
  },
  {
    name: 'flareBackgroundTop',
    type: 'boolean',
    defaultValue: false,
    advanced: true,
    showIf: "options.get('hasGradientTransition') === false",
    helperText: 'Adds a top-positioned teal gradient effect (Dark teal gradient - Figma #3).',
  },
  {
    name: 'hasGradientDark',
    type: 'boolean',
    defaultValue: false,
    advanced: true,
    showIf: "options.get('hasGradientTransition') === false",
    helperText: 'Adds a dark radial gradient background (Blue radial - Figma #4).',
  },
  {
    name: 'flareBackground',
    type: 'boolean',
    defaultValue: false,
    advanced: true,
    showIf: "options.get('hasGradientTransition') === false",
    helperText: 'Adds a flare effect as the background decoration for the section.',
  },
  {
    name: 'hasGradientTransitionTop',
    type: 'boolean',
    defaultValue: false,
    advanced: true,
    showIf: "options.get('hasGradientTransition') === false",
    helperText: 'Enable gradient at the top of section. Adds visual separation between sections.',
  },
  {
    name: 'hasGradientTransitionTopBanner',
    type: 'boolean',
    defaultValue: false,
    advanced: true,
    showIf: "options.get('hasGradientTransition') === false",
    helperText: 'Enable gradient at top, optimized for banner use cases. Adds visual separation between sections.',
  },
  {
    name: 'dottedBackground',
    type: 'boolean',
    defaultValue: false,
    advanced: true,
    showIf: "options.get('hasGradientTransition') === false",
    helperText: 'Enable a dotted pattern as the section background. Applies only if gradient transitions are off.',
  },
  {
    name: 'tealBackground',
    type: 'boolean',
    defaultValue: false,
    advanced: true,
    showIf: "options.get('hasGradientTransition') === false",
    helperText: 'Applies teal gradient background for locked content banners.',
  },
] as const satisfies DeepReadonly<Input[]>;

export const sectionProps = sectionFields.map(field => field.name) as string[];

export const splitSectionProps = <T>(props: T extends SectionConfigProps ? T : never) =>
  Object.entries(props).reduce(
    (acc, [key, value]) => {
      if (sectionProps.includes(key)) {
        return { ...acc, sectionProps: { ...acc.sectionProps, [key]: value } };
      }

      return { ...acc, rest: { ...acc.rest, [key]: value } };
    },
    { sectionProps: {} as SectionConfigProps, rest: {} as Omit<T, keyof SectionConfigProps> },
  );

export type SectionConfigProps = GenerateBuilder<typeof sectionFields>;

export default sectionFields;
